import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SEARCH_DEBOUNCE_MS } from '@/shared/config'
import { useDebouncedValue } from '@/shared/lib'
import { useCountryOptions } from '@/shared/lib/use-country-options'
import { AnimatedIcon, FloatingField, MultiSelect } from '@/shared/ui'
import { SearchIcon } from '@/shared/ui/icons/search'

import type { LeadSlice } from '../model/slice'
import { LEAD_SOURCE_KEYS } from '../model/source-options'

/*
 * No «Все» entry any more: nothing chosen *is* «все», and it is also the empty
 * parameter the URL and the backend already read that way. An explicit option
 * would be a second spelling of the same state.
 */
const TYPE_VALUES = ['lead', 'bot', 'abuse'] as const
const SERVED_VALUES = ['white', 'black'] as const

type LeadsFiltersProps = {
  slice: LeadSlice
  onSliceChange: (patch: Partial<LeadSlice>) => void
}

/**
 * The bar above the log.
 *
 * The search box holds an IP and nothing else. That is the one lookup worth
 * indexing on a table that grows by a row per hit: an address is selective on
 * its own, and a prefix of one is a subnet. Free text over user agents would
 * read the whole window on every keystroke.
 *
 * Keystrokes reach the URL through a debounce — every one of them is a request
 * otherwise — so the field keeps its own state and follows the slice back when
 * it changes from outside.
 */
export function LeadsFilters({ slice, onSliceChange }: LeadsFiltersProps) {
  const { t } = useTranslation(['leads', 'common'])
  const [search, setSearch] = useState(slice.q)
  const debounced = useDebouncedValue(search, SEARCH_DEBOUNCE_MS)

  // Adjusting state during render rather than in an effect: the field has to
  // follow the URL when it changes from outside — a back-button navigation, a
  // pasted link — and doing that in an effect would paint the stale value first.
  const [lastQ, setLastQ] = useState(slice.q)
  if (slice.q !== lastQ) {
    setLastQ(slice.q)
    setSearch(slice.q)
  }

  useEffect(() => {
    if (debounced !== slice.q) onSliceChange({ q: debounced })
    // `onSliceChange` is a fresh closure on every render, and `slice.q` is what
    // this writes; depending on either would re-run on every render instead of
    // on a settled keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  const countryOptions = useCountryOptions()

  return (
    <div className="flex flex-wrap items-center gap-3">
      <FloatingField
        size="sm"
        isMono
        label={t('filters.search')}
        icon={<AnimatedIcon icon={SearchIcon} />}
        className="w-44"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      <MultiSelect
        size="sm"
        label={t('columns.type')}
        className="w-40"
        value={slice.type}
        onValueChange={(next) => onSliceChange({ type: next })}
        options={TYPE_VALUES.map((value) => ({ value, label: t(`type.${value}`) }))}
      />

      <MultiSelect
        size="sm"
        label={t('filters.served')}
        className="w-40"
        value={slice.served}
        onValueChange={(next) => onSliceChange({ served: next })}
        options={SERVED_VALUES.map((value) => ({ value, label: t(`page.${value}`) }))}
      />

      <MultiSelect
        size="sm"
        label={t('columns.country')}
        className="w-48"
        value={slice.country}
        onValueChange={(next) => onSliceChange({ country: next })}
        options={countryOptions}
        hasCodes
      />

      <MultiSelect
        size="sm"
        label={t('columns.source')}
        className="w-48"
        value={slice.source}
        onValueChange={(next) => onSliceChange({ source: next })}
        options={LEAD_SOURCE_KEYS.map((key) => ({
          value: key,
          label: t(`common:source.${key}`),
        }))}
      />
    </div>
  )
}
